import {
  Modal,
  TextInput,
  NumberInput,
  MultiSelect,
  Select,
  Button,
  Group,
  Stack,
  FileInput,
  useMantineTheme,
  rem,
  Text,
} from '@mantine/core';
import Highlight from '@tiptap/extension-highlight';
import Underline from '@tiptap/extension-underline';

import { RichTextEditor } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import 'dayjs/locale/he';

import { useMediaQuery } from '@mantine/hooks';
import { DateInput, DatesProvider } from '@mantine/dates';
import { useForm } from '@mantine/form';
import {
  InventoryRecord,
  InventoryCategoriesRecord,
} from '../types/pocketbase-types';
import {
  IconUpload,
  IconCalendar,
  IconCurrencyShekel,
  IconCurrencyPound,
  IconCurrencyDollar,
  IconCurrencyEuro,
  IconNfc,
} from '@tabler/icons-react';
import { useEffect } from 'react';

const currencyIcons = {
  ILS: <IconCurrencyShekel size={16} />,
  USD: <IconCurrencyDollar size={16} />,
  EUR: <IconCurrencyEuro size={16} />,
  GBP: <IconCurrencyPound size={16} />,
};

interface EditInventoryModalProps {
  mode: 'create' | 'edit';
  record?: InventoryRecord & {
    expand: { categories: InventoryCategoriesRecord[] };
  };
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: FormData) => Promise<void>;
  categories: InventoryCategoriesRecord[];
  manufacturers: string[];
}

export default function EditInventoryModal({
  mode,
  record,
  isOpen,
  onClose,
  onSubmit,
  categories,
}: EditInventoryModalProps) {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

  const form = useForm({
    initialValues: {
      product_name: '',
      categories: [] as string[],
      manufacturer: '',
      model: '',
      catalog_number: '',
      original_catalog_number: '',
      quantity: 0,
      purchase_price: 0,
      purchase_price_currency: 'ILS',
      selling_price: 0,
      selling_price_currency: 'ILS',
      estimated_selling_price: 0,
      estimated_selling_price_currency: 'ILS',
      location: '',
      RFID: '',
      comments: '',
      purchase_date: null,
      arrival_date: null,
      image: null as File | null,
    },
  });

  // Update form values when record changes or modal opens
  useEffect(() => {
    if (record && isOpen) {
      console.log('Setting form values with record:', record);
      form.setValues({
        product_name: record.product_name || '',
        categories: record.expand?.categories?.map((cat) => cat.id) || [],
        manufacturer: record.manufacturer || '',
        model: record.model || '',
        catalog_number: record.catalog_number || '',
        original_catalog_number: record.original_catalog_number || '',
        quantity: record.quantity || 0,
        purchase_price: record.purchase_price || 0,
        purchase_price_currency: record.purchase_price_currency || 'ILS',
        selling_price: record.selling_price || 0,
        selling_price_currency: record.selling_price_currency || 'ILS',
        estimated_selling_price: record.estimated_selling_price || 0,
        estimated_selling_price_currency: record.estimated_selling_price_currency || 'ILS',
        location: record.location || '',
        RFID: record.RFID || '',
        comments: record.comments || '',
        purchase_date: record.purchase_date
          ? new Date(record.purchase_date)
          : null,
        arrival_date: record.arrival_date
          ? new Date(record.arrival_date)
          : null,
        image: null,
      });
    }
  }, [record, isOpen]);

  const handleSubmit = async (values: typeof form.values) => {
    try {
      const formData = new FormData();

      Object.entries(values).forEach(([key, value]) => {
        if (key !== 'image' && value !== null) {
          if (key === 'categories') {
            // Send category IDs as JSON string
            formData.append(key, JSON.stringify(value));
          } else if (value instanceof Date) {
            formData.append(key, value.toISOString());
          } else {
            formData.append(key, String(value));
          }
        }
      });

      if (values.image) {
        formData.append('image', values.image);
      }

      await onSubmit(formData);
      form.reset();
    } catch (error) {
      console.error('Form submission error:', error);
      throw error;
    }
  };

  // Reset form when modal closes
  const handleClose = () => {
    form.reset();
    onClose();
  };

  // Initialize rich text editor for comments
  const commentsEditor = useEditor({
    extensions: [StarterKit, Underline, Highlight],
    content: record?.comments || '',
    onUpdate: ({ editor }) => {
      form.setFieldValue('comments', editor.getHTML());
    },
  });
  useEffect(() => {
    if (commentsEditor && record?.comments) {
      commentsEditor.commands.setContent(record.comments);
    }
  }, [record?.comments, commentsEditor]);

  return (
    <Modal
      centered
      fullScreen={isMobile}
      opened={isOpen}
      onClose={handleClose}
      title={mode === 'create' ? 'הוספת פריט' : 'עריכת פריט'}
      size="xl"
      styles={{
        title: {
          fontWeight: 'bold',
          fontSize: '1.2rem',
        },
      }}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <DatesProvider
          settings={{
            locale: 'he',
            firstDayOfWeek: 0,
            weekendDays: [5, 6], // Friday and Saturday
            labelSeparator: ' ',
          }}
        >
          <Stack>
            <TextInput
              label="שם מוצר"
              required
              {...form.getInputProps('product_name')}
            />
            <Group grow>
              <TextInput label="דגם" {...form.getInputProps('model')} />
              <TextInput label="יצרן" {...form.getInputProps('manufacturer')} />
            </Group>

            <Group grow>
              <TextInput
                styles={{
                  input: {
                    textAlign: 'left',
                    direction: 'ltr',
                  },
                }}
                label="מק״ט"
                {...form.getInputProps('catalog_number')}
              />
              <TextInput
                label="מק״ט מקורי"
                styles={{
                  input: {
                    textAlign: 'left',
                    direction: 'ltr',
                  },
                }}
                {...form.getInputProps('original_catalog_number')}
              />
            </Group>

            <Group grow>
              <MultiSelect
                label="קטגוריות"
                data={categories.map((cat) => ({
                  value: cat.id,
                  label: cat.name,
                }))}
                value={form.values.categories}
                onChange={(value) => form.setFieldValue('categories', value)}
                searchable
              />
            </Group>
            <Group grow>
              <NumberInput
                hideControls
                label="מחיר רכישה"
                min={0}
                thousandSeparator=","
                rightSection={currencyIcons[form.values.purchase_price_currency]}
                leftSection={
                  <Select
                    allowDeselect={false}
                    withCheckIcon={false}
                    data={[
                      { value: 'ILS', label: '🇮🇱' },
                      { value: 'USD', label: '🇺🇸' },
                      { value: 'EUR', label: '🇪🇺' },
                      { value: 'GBP', label: '🇬🇧' },
                    ]}
                    {...form.getInputProps('purchase_price_currency')}
                    styles={{
                      input: {
                        direction: 'ltr',
                        textAlign: 'left',
                        width: 60,
                        fontSize: 24,
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                      },
                      option: {
                        display: 'block',
                        fontSize: 22,
                        textAlign: 'center',
                      },
                    }}
                  />
                }
                styles={{
                  input: {
                    direction: 'ltr',
                    textAlign: 'left',
                  },
                }}
                {...form.getInputProps('purchase_price')}
              />
              <NumberInput
                hideControls
                label="מחיר מכירה"
                thousandSeparator=","
                rightSection={currencyIcons[form.values.selling_price_currency]}
                leftSection={
                  <Select
                    allowDeselect={false}
                    withCheckIcon={false}
                    data={[
                      { value: 'ILS', label: '🇮🇱' },
                      { value: 'USD', label: '🇺🇸' },
                      { value: 'EUR', label: '🇪🇺' },
                      { value: 'GBP', label: '🇬🇧' },
                    ]}
                    {...form.getInputProps('selling_price_currency')}
                    styles={{
                      input: {
                        direction: 'ltr',
                        textAlign: 'left',
                        width: 60,
                        fontSize: 24,
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                      },
                      option: {
                        display: 'block',
                        fontSize: 22,
                        textAlign: 'center',
                      },
                    }}
                  />
                }
                styles={{
                  input: {
                    direction: 'ltr',
                    textAlign: 'left',
                  },
                }}
                {...form.getInputProps('selling_price')}
              />
            </Group>
            <Group grow>
              <NumberInput
                hideControls
                label="מחיר מכירה משוער"
                thousandSeparator=","
                rightSection={currencyIcons[form.values.estimated_selling_price_currency]}
                leftSection={
                  <Select
                    allowDeselect={false}
                    withCheckIcon={false}
                    data={[
                      { value: 'ILS', label: '🇮🇱' },
                      { value: 'USD', label: '🇺🇸' },
                      { value: 'EUR', label: '🇪🇺' },
                      { value: 'GBP', label: '🇬🇧' },
                    ]}
                    {...form.getInputProps('estimated_selling_price_currency')}
                    styles={{
                      input: {
                        direction: 'ltr',
                        textAlign: 'left',
                        width: 60,
                        fontSize: 24,
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                      },
                      option: {
                        display: 'block',
                        fontSize: 22,
                        textAlign: 'center',
                      },
                    }}
                  />
                }
                styles={{
                  input: {
                    direction: 'ltr',
                    textAlign: 'left',
                  },
                }}
                {...form.getInputProps('estimated_selling_price')}
              />
              <NumberInput
                label="כמות"
                placeholder="הכנס כמות"
                min={0}
                styles={{
                  input: {
                    direction: 'rtl',
                    textAlign: 'right',
                  },
                }}
                {...form.getInputProps('quantity')}
              />
            </Group>
            <Group grow>
              <TextInput
                label="RFID"
                leftSection={<IconNfc size={16} />}
                styles={{
                  root: {
                    direction: 'ltr',
                  },
                  input: {
                    textAlign: 'left',
                  },
                }}
                {...form.getInputProps('RFID')}
              />

              <FileInput
                label="תמונה"
                accept="image/*"
                rightSection={<IconUpload size={16} />}
                styles={{
                  input: {
                    textAlign: 'left',
                    direction: 'ltr',
                    paddingLeft: rem('28px'),
                  },
                }}
                {...form.getInputProps('image')}
              />
            </Group>
            <Group grow>
              <DateInput
                label="תאריך הגעה"
                valueFormat="DD/MM/YY"
                placeholder="בחר תאריך"
                clearable
                rightSection={<IconCalendar size={16} />}
                styles={{
                  input: {
                    direction: 'rtl',
                    textAlign: 'right',
                  },
                  day: {
                    height: '36px',
                  },
                }}
                monthLabelFormat="MMMM YYYY"
                monthsListFormat="MMMM"
                yearsListFormat="YYYY"
                {...form.getInputProps('arrival_date')}
              />
            </Group>

            <Group grow>
              <TextInput label="מיקום" {...form.getInputProps('location')} />
            </Group>

            <RichTextEditor editor={commentsEditor} variant="subtle">
              <RichTextEditor.Toolbar sticky stickyOffset={60}>
                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.Bold />
                  <RichTextEditor.Italic />
                  <RichTextEditor.Underline />
                  <RichTextEditor.Strikethrough />
                  <RichTextEditor.ClearFormatting />
                  <RichTextEditor.Highlight />
                </RichTextEditor.ControlsGroup>
              </RichTextEditor.Toolbar>
              <RichTextEditor.Content />
            </RichTextEditor>

            <Group justify="flex-end" mt="md">
              <Button variant="light" onClick={handleClose}>
                ביטול
              </Button>
              <Button type="submit" color="blue">
                {mode === 'create' ? 'הוסף פריט' : 'שמור שינויים'}
              </Button>
            </Group>
          </Stack>
        </DatesProvider>
      </form>
    </Modal>
  );
}
